/**
 * AssetSelectionStep Component
 *
 * Parameterized step for asset selection, reusable across wizards:
 * - Asset wizard step 1 (selecting main asset)
 * - Vulnerability wizard step 2 (selecting affected asset for custom vulnerabilities)
 *
 * Features:
 * - TanStack Table with name/status columns using SelectionTable
 * - Prepopulated with first 200 assets (alpha-sorted)
 * - Client-side filtering as user types
 * - Custom asset creation via "Add custom" option
 * - Input sanitization for security
 */

import { PlusIcon } from '@heroicons/react/24/outline';
import { useCallback, useMemo, useState } from 'react';

import { cn } from '@/utils/classname';

import { sortAssetsByClassMatch } from '@/sections/attacks/attackPaths/components/icons/utils/icon-to-class-mapping';
import {
  assetColumns,
  assetDefaultSort,
} from '@/sections/attacks/attackPaths/components/wizards/columns';
import {
  CurrentlyEditingDisplay,
  SearchEmptyState,
  SelectionTable,
  WizardSearchInput,
} from '@/sections/attacks/attackPaths/components/wizards/components';
import {
  type SearchableListItem,
  useSearchableList,
} from '@/sections/attacks/attackPaths/components/wizards/hooks';

/**
 * Props for AssetSelectionStep component
 */
interface AssetSelectionStepProps {
  /** Step title displayed at top - optional, hidden when not provided */
  title?: string;
  /** Step description/instructions */
  description: string;
  /** Current form data for asset selection */
  formData: {
    assetId: string;
    /** Human-readable display name for the asset */
    assetDisplayName?: string;
    /** IP address or group for secondary display */
    assetMetadata?: string;
    isCustom: boolean;
  };
  /** Callback when asset changes - includes display name and metadata for human-readable preview */
  onAssetChange: (
    assetId: string,
    isCustom: boolean,
    displayName: string,
    metadata?: string
  ) => void;
  /**
   * Optional vulnerability key to filter assets by.
   * When provided, only shows assets that have the HAS_VULNERABILITY
   * relationship to this risk.
   */
  vulnerabilityKey?: string;
  /**
   * Optional preselected asset key for edit mode.
   * When provided, ensures this asset is included in results
   * even if it would be outside the pagination window.
   */
  preselectedKey?: string;
  /**
   * Optional asset classes to prioritize in results.
   * Matching assets will be sorted to the top while all assets remain visible.
   * Used by AddNodeModal to soft-prioritize based on selected icon.
   */
  priorityClasses?: string[];
}

/**
 * Reusable asset selection step component.
 *
 * Provides a search input with SelectionTable for selecting existing Chariot assets
 * or adding custom non-Chariot assets. Used in Asset wizard (step 1) and
 * Vulnerability wizard (step 2 for custom vulnerabilities).
 */
export function AssetSelectionStep({
  title,
  description,
  formData,
  onAssetChange,
  vulnerabilityKey,
  preselectedKey,
  priorityClasses,
}: AssetSelectionStepProps) {
  const [searchValue, setSearchValue] = useState('');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState(
    formData.isCustom ? formData.assetDisplayName || '' : ''
  );
  const [customMetadata, setCustomMetadata] = useState(
    formData.isCustom ? formData.assetMetadata || '' : ''
  );

  // Fetch and filter data
  const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchableList({
      type: 'asset',
      searchTerm: searchValue,
      vulnerabilityKey,
      // Pass preselectedKey to ensure the selected item is always in the list (for edit mode auto-scroll)
      preselectedKey,
    });

  // Sort items by priority classes if provided (matching assets first)
  const sortedItems = useMemo(() => {
    if (!priorityClasses?.length) return items;
    // Extract class from originalData for sorting, then return SearchableListItem[]
    const itemsWithClass = items.map(item => ({
      ...item,
      class: (item.originalData?.class as string) ?? '',
    }));
    const sorted = sortAssetsByClassMatch(itemsWithClass, priorityClasses);
    // Map back to original items (preserving SearchableListItem type)
    return sorted.map(
      ({ class: _, ...rest }) => rest as (typeof items)[number]
    );
  }, [items, priorityClasses]);

  /**
   * Handle search change
   */
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  /**
   * Handle asset selection from table
   */
  const handleSelect = useCallback(
    (item: SearchableListItem) => {
      // Pass item.name as display name, item.metadata as secondary info (IP/group)
      onAssetChange(item.key, false, item.name, item.metadata);
      setSearchValue(''); // Clear search after selection
    },
    [onAssetChange]
  );

  // Dynamic row count: 8 rows in Add mode, 6 in Edit mode
  const isEditMode = Boolean(preselectedKey);
  const tableMaxRows = isEditMode ? 6 : 8;

  return (
    <div className="flex flex-col gap-4" data-testid="asset-selection-step">
      {/* Title - only shown when provided (e.g., for secondary steps like "Select Affected Asset") */}
      {title && (
        <label
          id="asset-search-label"
          className="text-chariot-text-primary text-lg font-semibold"
        >
          {title}
        </label>
      )}
      {/* Currently Editing Display (Edit Mode Only) */}
      {preselectedKey && formData.assetId && !formData.isCustom && (
        <CurrentlyEditingDisplay
          name={formData.assetDisplayName || formData.assetId}
          metadata={formData.assetMetadata}
          status={undefined}
          isCustom={false}
        />
      )}

      {/* Currently Selected Custom Asset */}
      {formData.isCustom && formData.assetId && (
        <CurrentlyEditingDisplay
          name={formData.assetDisplayName || formData.assetId}
          isCustom={true}
        />
      )}

      <p
        id="asset-search-description"
        className="text-chariot-text-secondary text-sm"
      >
        {description}
      </p>

      {/* Custom Asset Toggle */}
      {showCustomForm ? (
        <div
          className="border-chariot-stroke bg-chariot-background-secondary flex flex-col gap-3 rounded-lg border p-4"
          data-testid="custom-asset-form"
        >
          <div className="flex items-center justify-between">
            <p className="text-chariot-text-primary text-sm font-medium">
              Create custom asset
            </p>
            <button
              type="button"
              className="text-chariot-text-secondary hover:text-chariot-text-primary text-xs underline"
              onClick={() => {
                setShowCustomForm(false);
                setCustomName('');
                setCustomMetadata('');
                if (formData.isCustom) {
                  onAssetChange('', false, '', undefined);
                }
              }}
            >
              Search Chariot assets instead
            </button>
          </div>
          <input
            type="text"
            className={cn(
              'bg-chariot-background-primary border-chariot-stroke text-chariot-text-primary',
              'placeholder:text-chariot-text-tertiary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
            )}
            placeholder="Asset name (e.g., DC01, web-prod-03)"
            value={customName}
            onChange={e => {
              const name = e.target.value;
              setCustomName(name);
              if (name.trim()) {
                onAssetChange(
                  `custom-${name.trim()}`,
                  true,
                  name.trim(),
                  customMetadata.trim() || undefined
                );
              }
            }}
            autoFocus
            data-testid="custom-asset-name-input"
          />
          <input
            type="text"
            className={cn(
              'bg-chariot-background-primary border-chariot-stroke text-chariot-text-primary',
              'placeholder:text-chariot-text-tertiary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
            )}
            placeholder="Description (optional, e.g., 10.0.1.50, wraith.corp)"
            value={customMetadata}
            onChange={e => {
              const meta = e.target.value;
              setCustomMetadata(meta);
              if (customName.trim()) {
                onAssetChange(
                  `custom-${customName.trim()}`,
                  true,
                  customName.trim(),
                  meta.trim() || undefined
                );
              }
            }}
            data-testid="custom-asset-metadata-input"
          />
        </div>
      ) : (
        <button
          type="button"
          className={cn(
            'border-chariot-stroke bg-chariot-background-secondary hover:bg-chariot-background-secondary/80',
            'flex w-full items-center gap-2 rounded-lg border px-4 py-2.5 text-left transition-colors'
          )}
          onClick={() => setShowCustomForm(true)}
          data-testid="create-custom-asset-button"
        >
          <PlusIcon className="text-chariot-brand-primary size-4" />
          <span className="text-chariot-text-primary text-sm font-medium">
            Create custom asset
          </span>
          <span className="text-chariot-text-tertiary text-xs">
            (not linked to Chariot)
          </span>
        </button>
      )}

      {/* Search Input + Results Table - hidden when custom form is shown */}
      {!showCustomForm && (
        <>
          <WizardSearchInput
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search for a Chariot asset..."
            ariaLabel={title ? undefined : 'Search assets'}
            ariaLabelledBy={title ? 'asset-search-label' : undefined}
            ariaDescribedBy="asset-search-description"
            testId="asset-search-input"
            autoFocus
          />

          {/* Results Table or Empty State */}
          {items.length === 0 && searchValue.trim() ? (
            <SearchEmptyState
              searchTerm={searchValue}
              onClearSearch={() => setSearchValue('')}
              entityName="assets"
              testIdPrefix="asset-search-empty"
            />
          ) : (
            <SelectionTable
              data={sortedItems}
              columns={assetColumns}
              selectedKey={formData.isCustom ? null : formData.assetId}
              onSelect={handleSelect}
              isLoading={isLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={fetchNextPage}
              defaultSort={assetDefaultSort}
              maxRows={tableMaxRows}
              highlightSelected={!preselectedKey}
            />
          )}
        </>
      )}
    </div>
  );
}
